import React, { useEffect, useMemo, useState } from "react"
import _ from "lodash"
import { useNavigate } from "react-router-dom"
import { Button, IconButton, InputAdornment, LinearProgress, TextField, Tooltip } from "@mui/material"
import {
    Add,
    AddLinkOutlined,
    CalendarTodayOutlined,
    CheckOutlined,
    ContentCopyOutlined,
    DeleteOutline,
    EditOutlined,
    LinkOutlined,
    Search,
    SearchOff
} from "@mui/icons-material"

import { GetCurrentUser, local_url, production_url } from "../utils/hooks"
import { ConfirmationAlert } from "../components/index"
import { useSnackbar } from "../components/Snakebar"
import EmptyState from "../Lectures/components/EmptyState"
import LinkFormDialog from "./components/LinkFormDialog"
import { useLazyGetInstitutionClassesQuery } from "../Redux/actions/classSetup.action"
import {
    useCreateAdmissionLinkMutation,
    useDeleteAdmissionLinkMutation,
    useEditAdmissionLinkMutation,
    useLazyGetAllAdmissionLinkQuery
} from "../Redux/actions/admissionSetup.action"

const buttonSx = { textTransform: "none", borderRadius: "10px", whiteSpace: "nowrap" }

const linkUrl = (link) => {
    const base = process.env.NODE_ENV === "production" ? production_url : local_url
    return `${base}/registrations/${link.institution_ref}/${link.class_ref}/${link.id}`
}

const formatDate = (value) => {
    if (!value) return "-"
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return String(value)
    return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
}

/* A link stays open through the whole of its expiry day. */
const isExpired = (link) => {
    const date = new Date(String(link.expired || "").split("T")[0])
    if (Number.isNaN(date.getTime())) return false
    date.setHours(23, 59, 59, 999)
    return date < new Date()
}

/* navigator.clipboard only exists on secure origins; the app is also served over plain http. */
const copyText = async (text) => {
    if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
    }
    const field = document.createElement("textarea")
    field.value = text
    field.setAttribute("readonly", "")
    field.style.position = "fixed"
    field.style.opacity = "0"
    document.body.appendChild(field)
    field.select()
    const copied = document.execCommand("copy")
    field.remove()
    if (!copied) throw new Error("copy failed")
}

function LinkCard({ link, className, copied, onCopy, onEdit, onDelete }) {
    const expired = isExpired(link)
    return (
        <li className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:flex-row sm:items-center sm:gap-4">
            <span
                className={[
                    "hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl sm:flex",
                    expired ? "bg-slate-100 text-slate-400" : "bg-indigo-50 text-indigo-600"
                ].join(" ")}
            >
                <LinkOutlined />
            </span>

            <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                    <h3 className="truncate font-semibold text-slate-900">{link.title || "Untitled link"}</h3>
                    <span
                        className={[
                            "rounded-full px-2 py-0.5 text-xs font-medium",
                            expired ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-700"
                        ].join(" ")}
                    >
                        {expired ? "Expired" : "Active"}
                    </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    <span className="font-medium text-slate-700">{className || "Unknown class"}</span>
                    <span className="inline-flex items-center gap-1">
                        <CalendarTodayOutlined sx={{ fontSize: 14 }} />
                        {expired ? "Expired" : "Expires"} {formatDate(link.expired)}
                    </span>
                    <span className="text-slate-400">Created {formatDate(link.created)}</span>
                </div>
            </div>

            <div className="flex items-center gap-1.5">
                <Button
                    size="small"
                    variant={copied ? "contained" : "outlined"}
                    color={copied ? "success" : "primary"}
                    disableElevation
                    startIcon={copied ? <CheckOutlined fontSize="small" /> : <ContentCopyOutlined fontSize="small" />}
                    onClick={() => onCopy(link)}
                    sx={{ ...buttonSx, borderRadius: "8px", minWidth: 104 }}
                >
                    {copied ? "Copied" : "Copy link"}
                </Button>
                <Tooltip title="Edit link" arrow>
                    <IconButton size="small" aria-label={`Edit ${link.title}`} onClick={() => onEdit(link)}>
                        <EditOutlined fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete link" arrow>
                    <IconButton size="small" color="error" aria-label={`Delete ${link.title}`} onClick={() => onDelete(link)}>
                        <DeleteOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            </div>
        </li>
    )
}

export default function CreateRegistrationLink() {
    const navigate = useNavigate()
    const { showSnackbar } = useSnackbar()
    const schoolId = useMemo(() => _.get(GetCurrentUser(), "meta_data.user_platform", null), [])

    const [search, setSearch] = useState("")
    const [form, setForm] = useState({ open: false, link: null })
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [copiedId, setCopiedId] = useState(null)

    const [getClasses, classesState] = useLazyGetInstitutionClassesQuery()
    const [getLinks, linksState] = useLazyGetAllAdmissionLinkQuery()
    const [createLink, createState] = useCreateAdmissionLinkMutation()
    const [editLink, editState] = useEditAdmissionLinkMutation()
    const [deleteLink, deleteState] = useDeleteAdmissionLinkMutation()

    const fetchAll = () => {
        getClasses({ institution_ref: schoolId })
        getLinks({ institution_ref: schoolId })
    }

    useEffect(() => {
        if (!GetCurrentUser()) {
            navigate("/auth")
            return
        }
        if (!schoolId) {
            showSnackbar({ message: "User not allow for this route", severity: "info" })
            navigate("/auth")
            return
        }
        fetchAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!copiedId) return
        const timer = setTimeout(() => setCopiedId(null), 2000)
        return () => clearTimeout(timer)
    }, [copiedId])

    const classes = useMemo(() => {
        const list = _.get(classesState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [classesState])

    const classNames = useMemo(
        () => Object.fromEntries(classes.map((item) => [item.id, item.class_name])),
        [classes]
    )

    const links = useMemo(() => {
        const list = _.get(linksState, "data.list", [])
        if (!Array.isArray(list)) return []
        // Active links first, then the soonest to expire
        return [...list].sort((a, b) =>
            isExpired(a) - isExpired(b) || String(a.expired).localeCompare(String(b.expired))
        )
    }, [linksState])

    const visibleLinks = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return links
        return links.filter((link) =>
            [link.title, classNames[link.class_ref]].some((value) => String(value || "").toLowerCase().includes(term))
        )
    }, [links, search, classNames])

    const activeCount = links.filter((link) => !isExpired(link)).length

    /* ------------------------------ actions ----------------------------- */

    const copyLink = async (link) => {
        try {
            await copyText(linkUrl(link))
            setCopiedId(link.id)
        } catch {
            showSnackbar({ message: "Couldn't copy the link. Please copy it manually.", severity: "error" })
        }
    }

    const saveLink = async ({ title, classRef, expiryDate }) => {
        const classItem = classes.find((item) => item.id === classRef)
        if (!classItem) return
        const body = {
            title,
            expired: expiryDate,
            institution_ref: classItem.institution_ref,
            class_ref: classRef
        }
        try {
            const result = form.link
                ? await editLink({ ...body, link_id: form.link.id }).unwrap()
                : await createLink(body).unwrap()
            showSnackbar({
                message: _.get(result, "message") || (form.link ? "Link updated." : "Link created."),
                severity: "success"
            })
            setForm({ open: false, link: null })
            getLinks({ institution_ref: schoolId })
        } catch {
            showSnackbar({
                message: form.link ? "Error while update the link, try again" : "Error while create link!",
                severity: "error"
            })
        }
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        try {
            const result = await deleteLink({ link_id: deleteTarget.id }).unwrap()
            showSnackbar({ message: _.get(result, "message") || "Link deleted.", severity: "success" })
            getLinks({ institution_ref: schoolId })
        } catch {
            showSnackbar({ message: "Error while delete the link", severity: "error" })
        }
        setDeleteTarget(null)
    }

    /* ------------------------------- view ------------------------------- */

    const loading = linksState.isFetching || classesState.isFetching
    const firstLoad = (linksState.isLoading || classesState.isLoading) && links.length === 0

    const renderLinks = () => {
        if (firstLoad) {
            return (
                <ul className="space-y-3">
                    {[0, 1, 2].map((key) => (
                        <li key={key} className="h-[84px] animate-pulse rounded-2xl bg-slate-100" />
                    ))}
                </ul>
            )
        }
        if (linksState.isError) {
            return (
                <EmptyState
                    icon={<AddLinkOutlined />}
                    title="Couldn't load registration links"
                    description="Please check your connection and try again."
                    actionLabel="Retry"
                    onAction={fetchAll}
                />
            )
        }
        if (links.length === 0) {
            return (
                <EmptyState
                    icon={<AddLinkOutlined />}
                    title="No registration links yet"
                    description={
                        classes.length === 0
                            ? "Create a class first, then share a link so students can register for it."
                            : "Create a link for a class and share it with students so they can register online."
                    }
                    actionLabel={classes.length > 0 ? "Create link" : undefined}
                    onAction={classes.length > 0 ? () => setForm({ open: true, link: null }) : undefined}
                />
            )
        }
        if (visibleLinks.length === 0) {
            return (
                <EmptyState
                    icon={<SearchOff />}
                    title="No links match"
                    description="Try a different title or class name."
                    actionLabel="Clear search"
                    onAction={() => setSearch("")}
                />
            )
        }
        return (
            <ul className="space-y-3">
                {visibleLinks.map((link) => (
                    <LinkCard
                        key={link.id}
                        link={link}
                        className={classNames[link.class_ref]}
                        copied={copiedId === link.id}
                        onCopy={copyLink}
                        onEdit={(item) => setForm({ open: true, link: item })}
                        onDelete={setDeleteTarget}
                    />
                ))}
            </ul>
        )
    }

    return (
        <div className="mx-auto w-full max-w-5xl">
            <div className="h-1">{loading && !firstLoad && <LinearProgress sx={{ height: 3, borderRadius: 2 }} />}</div>

            {/* Toolbar */}
            <div className="mb-5 mt-2 flex flex-col gap-3 sm:flex-row sm:items-center">
                <div className="min-w-0 flex-1">
                    <p className="text-sm text-slate-500">
                        <span className="font-semibold text-slate-900">{activeCount}</span> active
                        {" · "}
                        <span className="tabular-nums">{links.length}</span> total
                    </p>
                </div>
                {links.length > 0 && (
                    <TextField
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search links"
                        inputProps={{ "aria-label": "Search links" }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            )
                        }}
                        sx={{ width: { sm: 260 }, "& .MuiOutlinedInput-root": { borderRadius: "10px", bgcolor: "#fff" } }}
                    />
                )}
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<Add />}
                    onClick={() => setForm({ open: true, link: null })}
                    disabled={classes.length === 0}
                    sx={buttonSx}
                >
                    New link
                </Button>
            </div>

            {renderLinks()}

            <LinkFormDialog
                open={form.open}
                link={form.link}
                classes={classes}
                saving={createState.isLoading || editState.isLoading}
                onClose={() => setForm({ open: false, link: null })}
                onSubmit={saveLink}
            />

            <ConfirmationAlert
                open={Boolean(deleteTarget)}
                title="Delete registration link?"
                message={`"${deleteTarget?.title || "This link"}" will stop working for anyone who has it. This can't be undone.`}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={deleteState.isLoading ? undefined : confirmDelete}
            />
        </div>
    )
}
